import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { imgUrl, formatApiErrorDetail } from "@/lib/api";
import { PaymentDetails } from "@/components/PaymentDetails";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";
const FIELDS = [
  ["account_name", "Account holder name"],
  ["bank_name", "Bank name"],
  ["account_number", "Account number"],
  ["account_type", "Account type (e.g. Savings)"],
  ["ifsc", "IFSC code"],
  ["upi_id", "UPI ID"],
];

export const PaymentsTab = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    api.get("/settings/payment").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadQr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm({ ...form, qr_image: data.path });
      toast.success("QR code uploaded — save to apply");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key, updated_at, ...payload } = form;
      await api.put("/admin/settings/payment", payload);
      toast.success("Payment details updated");
      setVersion((v) => v + 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="h-40 border border-white/10 animate-pulse" />;
  const qr = form.qr_image?.startsWith("/") ? form.qr_image : imgUrl(form.qr_image);

  return (
    <div data-testid="admin-payments-tab" className="grid lg:grid-cols-2 gap-16">
      <form onSubmit={save} className="space-y-5" data-testid="payment-settings-form">
        <h2 className="font-display text-2xl tracking-tight">Payment Details</h2>
        <p className="text-sm text-white/50">These bank / UPI details and QR code are shown to investors on the payment step.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          {FIELDS.map(([k, label]) => (
            <input key={k} data-testid={`payment-${k.replace(/_/g, "-")}-input`} required placeholder={label} value={form[k] || ""} onChange={set(k)} className={inputCls} />
          ))}
        </div>
        <textarea data-testid="payment-instructions-input" rows={3} placeholder="Instructions shown to investors (optional)" value={form.instructions || ""} onChange={set("instructions")} className={inputCls} />
        <div className="flex items-center gap-4">
          <label className="border border-white/20 px-5 py-3 font-accent text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors cursor-pointer" data-testid="payment-qr-upload-label">
            {uploading ? "Uploading…" : "Upload New QR Code"}
            <input data-testid="payment-qr-upload-input" type="file" accept="image/*" className="hidden" onChange={uploadQr} disabled={uploading} />
          </label>
          {form.qr_image && <img src={qr} alt="UPI QR" className="w-14 h-14 object-contain bg-white p-1" data-testid="payment-qr-preview" />}
        </div>
        <button data-testid="payment-settings-save" disabled={saving || uploading} className="w-full border border-[#D4AF37] text-[#D4AF37] py-3 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
          {saving ? "Saving…" : "Save Payment Details"}
        </button>
      </form>
      <div>
        <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-white/50 mb-4">Investor preview</p>
        <PaymentDetails key={version} />
      </div>
    </div>
  );
};
